import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { X, Copy, Check, Link as LinkIcon, ShieldOff, Calendar } from "lucide-react";

const EXPIRY_OPTIONS = [
  { value: 7, key: "share.expiry.7" },
  { value: 30, key: "share.expiry.30" },
  { value: 90, key: "share.expiry.90" },
  { value: 0, key: "share.expiry.never" },
];

export const ShareModal = ({ open, onClose, projectId, projectName }) => {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [expiry, setExpiry] = useState(30);

  const createOrUpdate = useCallback((days) => {
    setBusy(true);
    api.post(`/projects/${projectId}/share`, { expires_in_days: days })
      .then((r) => { setToken(r.data.token); setExpiresAt(r.data.expires_at); })
      .catch((err) => {
        if (err?.response?.status === 402) toast.error(t("share.paid_only", "Only paid reports can be shared"));
        else toast.error(t("share.create_error", "Could not create share link"));
        onClose();
      })
      .finally(() => setBusy(false));
  }, [projectId, onClose, t]);

  useEffect(() => {
    if (!open) {
      setToken(null);
      setCopied(false);
      setExpiresAt(null);
      return;
    }
    // Fires once when the modal opens with the current expiry. Subsequent expiry
    // changes call createOrUpdate directly via changeExpiry(). We intentionally
    // do not depend on createOrUpdate to avoid re-firing when the parent re-renders.
    createOrUpdate(expiry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const url = token ? `${window.location.origin}/shared/${token}` : "";
  const expiryLabel = expiresAt ? new Date(expiresAt).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : t("share.expiry.never");

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("Could not copy — please copy manually"); }
  };

  const revoke = async () => {
    setRevoking(true);
    try {
      await api.post(`/projects/${projectId}/share/revoke`);
      toast.success(t("share.revoked", "Link revoked"));
      setToken(null);
      onClose();
    } catch { toast.error("Could not revoke"); }
    setRevoking(false);
  };

  const changeExpiry = (days) => {
    setExpiry(days);
    if (token) createOrUpdate(days);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(22,33,29,0.55)" }} data-testid="share-modal">
      <div className="card p-8 w-[95%] max-w-lg relative" style={{ background: "var(--white)" }}>
        <button onClick={onClose} className="absolute top-4 right-4" style={{ color: "var(--muted)" }} data-testid="share-close">
          <X size={18} />
        </button>
        <div className="flex items-center gap-3 mb-2">
          <LinkIcon size={18} style={{ color: "var(--brand-green)" }} />
          <div className="overline">{t("share.overline")}</div>
        </div>
        <h3 className="font-display text-2xl mb-1">{t("share.title")}</h3>
        <p className="text-sm mb-6" style={{ color: "var(--muted)" }}>
          {t("share.desc_prefix")} <b>{projectName}</b> {t("share.desc_suffix")}
        </p>

        {/* Expiry selector */}
        <div className="mb-5">
          <label className="label-field flex items-center gap-2">
            <Calendar size={14} /> {t("share.expiry")}
          </label>
          <div className="grid grid-cols-4 gap-2 mt-1">
            {EXPIRY_OPTIONS.map((o) => (
              <button
                key={o.value}
                onClick={() => changeExpiry(o.value)}
                disabled={busy}
                className={`p-2 rounded-md border text-sm transition-colors ${expiry === o.value ? "border-[var(--brand-green)] bg-[rgba(26,111,43,0.06)] text-[var(--charcoal)] font-medium" : "border-[var(--hairline)] hover:border-[var(--brand-green)]"}`}
                data-testid={`share-expiry-${o.value}`}
              >
                {t(o.key)}
              </button>
            ))}
          </div>
          {expiresAt && (
            <div className="text-xs font-mono mt-2" style={{ color: "var(--muted)" }} data-testid="share-expiry-date">
              expires {expiryLabel}
            </div>
          )}
        </div>

        {busy ? (
          <div className="text-sm" style={{ color: "var(--muted)" }}>{t("share.generating")}</div>
        ) : token ? (
          <>
            <div className="flex items-center gap-2 p-3 rounded-md" style={{ background: "var(--paper)", border: "1px solid var(--hairline)" }}>
              <input
                className="flex-1 bg-transparent font-mono text-xs outline-none"
                value={url}
                readOnly
                onFocus={(e) => e.target.select()}
                data-testid="share-url"
              />
              <button onClick={copy} className="btn-secondary !py-1 !px-3 text-xs" data-testid="share-copy">
                {copied ? <><Check size={12} /> {t("share.copied")}</> : <><Copy size={12} /> {t("share.copy")}</>}
              </button>
            </div>
            <div className="flex items-center justify-between mt-6">
              <a href={url} target="_blank" rel="noopener noreferrer" className="link text-sm" data-testid="share-preview">{t("share.preview")}</a>
              <button onClick={revoke} disabled={revoking} className="btn-ghost !p-0 text-sm" style={{ color: "var(--orange-spark)" }} data-testid="share-revoke">
                <ShieldOff size={12} className="inline" /> {revoking ? t("share.revoking") : t("share.revoke")}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};
